import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Nomenclature } from '../models/models';
import api from './api';

interface NomenclatureState {
  data:           Nomenclature[];
  total:          number;
  totalPages:     number;
  currentPage:    number;
  loading:        boolean;
  error:          string | null;
  successMessage: string | null;
  selectedNomenclature: Nomenclature | null;
}

const initialState: NomenclatureState = {
  data:           [],
  total:          0,
  totalPages:     0,
  currentPage:    0,
  loading:        false,
  error:          null,
  successMessage: null,
  selectedNomenclature: null,
};

export const fetchNomenclatures = createAsyncThunk<
  { data: Nomenclature[]; total: number; totalPages: number; currentPage: number },
  { article_parent_id?: number | null; limit?: number; offset?: number },
  { rejectValue: string }
>('nomenclature/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/nomenclature', {
      params: {
        article_parent_id: params.article_parent_id ?? null,
        limit:             params.limit  ?? 20,
        offset:            params.offset ?? 0,
      },
    });
    return data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message ?? 'Échec chargement nomenclatures');
  }
});

export const fetchNomenclatureByParent = createAsyncThunk<
  Nomenclature[],
  number,
  { rejectValue: string }
>('nomenclature/fetchByParent', async (article_parent_id, { rejectWithValue }) => {
  try {
    const { data } = await api.get(`/nomenclature/byParent/${article_parent_id}`);
    return (data.data ?? []) as Nomenclature[];
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message ?? 'Échec chargement composants');
  }
});

export const createOrUpdateNomenclature = createAsyncThunk<
  Nomenclature,
  Nomenclature,
  { rejectValue: string }
>('nomenclature/createOrUpdate', async (nomen, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/nomenclature', nomen);
    return data as Nomenclature;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message ?? 'Échec sauvegarde nomenclature');
  }
});

export const deleteNomenclature = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('nomenclature/delete', async (id, { rejectWithValue }) => {
  try {
    await api.delete('/nomenclature', { params: { id } });
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message ?? 'Échec suppression');
  }
});

const NomenclatureSlice = createSlice({
  name: 'nomenclature',
  initialState,
  reducers: {
    setSelectedNomenclature(state, action) { state.selectedNomenclature = action.payload; },
    clearNomenclatureData(state) { state.data = []; state.total = 0; },
    clearMessages(state) { state.error = null; state.successMessage = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNomenclatures.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchNomenclatures.fulfilled, (state, action) => {
        state.loading      = false;
        state.data         = action.payload.data;
        state.total        = action.payload.total;
        state.totalPages   = action.payload.totalPages;
        state.currentPage  = action.payload.currentPage;
      })
      .addCase(fetchNomenclatures.rejected,  (state, action) => { state.loading = false; state.error = action.payload ?? 'Erreur'; })

      .addCase(createOrUpdateNomenclature.fulfilled, (state) => { state.successMessage = 'Nomenclature enregistrée.'; })
      .addCase(createOrUpdateNomenclature.rejected,  (state, action) => { state.error = action.payload ?? 'Erreur'; })

      .addCase(deleteNomenclature.fulfilled, (state) => { state.successMessage = 'Nomenclature supprimée.'; })
      .addCase(deleteNomenclature.rejected,  (state, action) => { state.error = action.payload ?? 'Erreur'; });
  },
});

export const { setSelectedNomenclature, clearNomenclatureData, clearMessages } = NomenclatureSlice.actions;
export default NomenclatureSlice.reducer;
